import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { trpc } from '@/trpc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, CheckCircle2, Loader } from 'lucide-react';
import { toast } from 'sonner';
import type { SectionRecommendation } from 'types';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/resumes_/$resumeId/recommendations')({
  component: ResumeRecommendationsPage,
});

function ResumeRecommendationsPage() {
  const { resumeId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  // Fetch existing recommendations or generate new ones
  const recommendationsQuery = trpc.resumeRecommendations.get.useQuery(
    { resumeId: parseInt(resumeId) },
    { enabled: !!user?.id },
  );

  const generateMutation = trpc.resumeRecommendations.generate.useMutation({
    onSuccess: (_data: unknown) => {
      toast.success('Resume recommendations generated!');
      recommendationsQuery.refetch();
    },
    onError: (_error: unknown) => {
      toast.error('Failed to generate recommendations');
    },
  });

  const handleReturnToProfile = () => {
    navigate({ to: '/dashboard/profile' });
  };

  // Fire the analytics event whenever recommendations are actually shown —
  // freshly generated OR loaded from cache. The mutation onSuccess alone
  // wouldn't catch the cached case.
  const recsTrackedRef = useRef(false);
  useEffect(() => {
    if (
      !recsTrackedRef.current &&
      recommendationsQuery.data?.recommendations
    ) {
      analytics.track('resume_recommendations_generated');
      recsTrackedRef.current = true;
    }
  }, [recommendationsQuery.data?.recommendations]);

  // Auto-start generation if needed
  useEffect(() => {
    // Only proceed if query has finished loading
    if (recommendationsQuery.isLoading || recommendationsQuery.isFetching) {
      return;
    }

    const recommendations = recommendationsQuery.data?.recommendations;
    const needsRegeneration = recommendationsQuery.data?.needsRegeneration;
    const isGenerating = generateMutation.isLoading;

    console.error('🔍 Checking if generation needed:', {
      hasRecommendations: !!recommendations,
      needsRegeneration,
      isGenerating,
      isLoading: recommendationsQuery.isLoading,
      isFetching: recommendationsQuery.isFetching,
    });

    // Only auto-generate if we don't have recommendations and we're not already generating
    if ((!recommendations || needsRegeneration) && !isGenerating) {
      console.error('🚀 Auto-starting generation');
      generateMutation.mutate({ resumeId: parseInt(resumeId) });
    }
  }, [
    recommendationsQuery.isLoading,
    recommendationsQuery.isFetching,
    recommendationsQuery.data?.recommendations,
    recommendationsQuery.data?.needsRegeneration,
    generateMutation.isLoading,
    resumeId,
  ]);

  // Loading state
  if (recommendationsQuery.isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToProfile}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Return to Profile
          </Button>
          <div className='flex items-center justify-center h-96'>
            <p className='text-muted-foreground'>Loading recommendations...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (recommendationsQuery.error) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToProfile}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Return to Profile
          </Button>
          <Card className='p-6 border-destructive'>
            <p className='text-destructive font-semibold'>
              Error loading recommendations
            </p>
            <p className='text-sm text-muted-foreground mt-2'>
              Please try again or contact support if the problem persists.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const recommendations = recommendationsQuery.data?.recommendations;
  const needsRegeneration = recommendationsQuery.data?.needsRegeneration;

  // If recommendations need to be generated (show processing indicator)
  if (!recommendations || needsRegeneration) {
    return (
      <div className='min-h-screen bg-gray-50 p-6'>
        <div className='max-w-7xl mx-auto'>
          <Button
            variant='outline'
            onClick={handleReturnToProfile}
            className='mb-6 flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Return to Profile
          </Button>
          <Card className='p-8'>
            <div className='flex flex-col items-center justify-center min-h-96 gap-6'>
              <div className='flex items-center justify-center'>
                <Loader className='h-8 w-8 animate-spin text-blue-600' />
              </div>
              <div className='text-center'>
                <h1 className='text-2xl font-bold mb-2'>
                  Generating Recommendations
                </h1>
                <p className='text-muted-foreground'>
                  {needsRegeneration
                    ? "Your resume has been updated. We're analyzing it now..."
                    : "We're analyzing your resume and creating personalized recommendations based on your career goals..."}
                </p>
              </div>
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <div className='flex gap-1'>
                  <div className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'></div>
                  <div
                    className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'
                    style={{ animationDelay: '0.1s' }}
                  ></div>
                  <div
                    className='h-2 w-2 bg-blue-600 rounded-full animate-bounce'
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Display recommendations
  const recommendationsArray: SectionRecommendation[] = Array.isArray(
    recommendations,
  )
    ? recommendations
    : [];
  const withRecommendations = recommendationsArray.filter(
    (r) => r.hasRecommendation,
  );
  const withoutRecommendations = recommendationsArray.filter(
    (r) => !r.hasRecommendation,
  );

  return (
    <div className='min-h-screen bg-gray-50 p-6'>
      <div className='max-w-7xl mx-auto'>
        <Button
          variant='outline'
          onClick={handleReturnToProfile}
          className='mb-6 flex items-center gap-2'
        >
          <ArrowLeft className='h-4 w-4' />
          Return to Profile
        </Button>

        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>
            Resume Recommendations
          </h1>
          <p className='text-gray-600 mt-2'>
            Personalized suggestions to strengthen your resume and better align
            with your career goals.
          </p>
        </div>

        <div className='space-y-6'>
          {/* Sections with recommendations */}
          {withRecommendations.length > 0 && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                Sections with Recommendations ({withRecommendations.length})
              </h2>
              <div className='space-y-4'>
                {withRecommendations.map((rec, idx) => (
                  <Card key={idx} className='p-6'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      {rec.sectionName}
                    </h3>

                    <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded'>
                      <p className='text-sm text-blue-900 font-medium mb-1'>
                        Justification:
                      </p>
                      <p className='text-sm text-blue-800'>
                        {rec.justification}
                      </p>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      {/* Original */}
                      <div>
                        <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                          Current
                        </h4>
                        <div className='bg-gray-100 p-3 rounded text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-xs'>
                          {rec.sectionText}
                        </div>
                      </div>

                      {/* Recommended */}
                      <div>
                        <h4 className='text-sm font-semibold text-green-700 mb-2'>
                          Recommended
                        </h4>
                        <div className='bg-green-50 p-3 rounded text-sm text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-xs border-l-4 border-green-500'>
                          {rec.recommendedText}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(rec.recommendedText ?? '');
                        toast.success('Copied to clipboard!');
                      }}
                      className='mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium'
                    >
                      Copy Recommended Text
                    </button>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Sections without recommendations */}
          {withoutRecommendations.length > 0 && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                Sections Looking Strong ({withoutRecommendations.length})
              </h2>
              <div className='space-y-3'>
                {withoutRecommendations.map((rec, idx) => (
                  <Card key={idx} className='p-4 bg-green-50 border-green-200'>
                    <div className='flex items-center gap-3'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0' />
                      <div>
                        <h3 className='font-semibold text-green-900'>
                          {rec.sectionName}
                        </h3>
                        <p className='text-sm text-green-700'>
                          No changes needed - this section is strong!
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
